/**
 * `ships.json extends` (M10 P1d): a hull family is a base def plus overrides. An entry with `extends` takes its
 * base's merged fields, then its `flightBand` preset (bands.json), then its own fields, shallowly (a nested
 * field such as `maxRateDeg` or `loadout` is replaced whole). Chains are allowed; an unknown base or a cycle
 * throws naming the defs. The merged entry keeps `extends` (the base id) and is checked by the full refined
 * `shipDefRawSchema`. File order is kept: def indices are used (`career.start.shipDefIndex`).
 */
import { flightPresetOf } from './bands'
import { shipDefRawSchema, shipsFileSchema, type FlightPreset, type ShipBand, type ShipDefRaw, type ShipsFileEntry } from './schemas'
import shipsJson from './ships.json'

type Entry = Readonly<Record<string, unknown>> & { readonly id: string; readonly extends?: string; readonly flightBand?: ShipBand }
type PresetOf = (band: ShipBand) => FlightPreset | undefined

function assign(target: Record<string, unknown>, source: Readonly<Record<string, unknown>>): void {
  for (const [k, v] of Object.entries(source)) if (v !== undefined) target[k] = v
}

/**
 * Merges every `extends` entry over its base (base → `flightBand` preset → own fields), in file order.
 * Entries without `extends` or `flightBand` come back as equal copies. The result is not yet validated.
 */
export function mergeShipExtends(entries: readonly Entry[], presetOf: PresetOf = (b) => flightPresetOf(b)): Record<string, unknown>[] {
  const byId = new Map(entries.map((e) => [e.id, e]))
  const merged = new Map<string, Record<string, unknown>>()
  const visiting: string[] = []

  const resolve = (entry: Entry): Record<string, unknown> => {
    const done = merged.get(entry.id)
    if (done) return done
    if (visiting.includes(entry.id)) throw new Error(`ships.json: extends cycle ${[...visiting.slice(visiting.indexOf(entry.id)), entry.id].join(' → ')}`)
    visiting.push(entry.id)
    const out: Record<string, unknown> = {}
    if (entry.extends !== undefined) {
      const base = byId.get(entry.extends)
      if (!base) throw new Error(`ships.json: ship ${entry.id} extends unknown base ${entry.extends}`)
      assign(out, resolve(base))
    }
    if (entry.flightBand !== undefined) {
      const preset = presetOf(entry.flightBand)
      if (!preset) throw new Error(`ships.json: ship ${entry.id} names flightBand ${entry.flightBand}, which has no preset in bands.json`)
      assign(out, preset)
    }
    assign(out, entry)
    visiting.pop()
    merged.set(entry.id, out)
    return out
  }

  return entries.map((e) => structuredClone(resolve(e)))
}

/** Merges and validates parsed file entries into full raw defs (throws a ZodError naming the bad def field). */
export function mergeShipEntries(entries: readonly ShipsFileEntry[], presetOf?: PresetOf): ShipDefRaw[] {
  return mergeShipExtends(entries, presetOf).map((e) => shipDefRawSchema.parse(e))
}

/** `ships.json` parsed, merged and validated: the list every raw reader should use once an `extends` entry exists. */
export function mergedShipsRaw(raw: unknown = shipsJson): ShipDefRaw[] {
  return mergeShipEntries(shipsFileSchema.parse(raw).ships)
}
